import { useEffect, useRef, useState } from "react";
import { PROCESS_DETAIL_STEPS } from "#/lib/constants";
import { img } from "#/lib/images";

/**
 * /process — scroll-driven step walkthrough mirrored from the Rise
 * "how it works" reference (sticky numbered sidebar, hairline divider,
 * step blocks with sub-points + one large stage photo). Palette swapped
 * per project rule: the reference's accent maps to the brand flame token. The sidebar highlights the
 * block in the middle band of the viewport via one IntersectionObserver;
 * clicking a number smooth-scrolls to its block. No library.
 */
export function ProcessSteps() {
  const [active, setActive] = useState(0);
  const blockRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const blocks = blockRefs.current.filter(
      (el): el is HTMLElement => el !== null,
    );
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(blocks.indexOf(entry.target as HTMLElement));
          }
        }
      },
      // A block is "active" while it crosses the middle 10% of the viewport.
      { rootMargin: "-45% 0px -45% 0px" },
    );
    for (const block of blocks) observer.observe(block);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-light-bg py-16 lg:py-24">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[300px_1px_1fr] lg:gap-12 lg:px-8">
        {/* Sticky numbered sidebar (desktop only) */}
        <nav aria-label="Process steps" className="hidden lg:block">
          <ol className="sticky top-36 m-0 flex list-none flex-col gap-7 p-0">
            {PROCESS_DETAIL_STEPS.map((step, i) => (
              <li key={step.number}>
                <button
                  type="button"
                  aria-current={i === active ? "step" : undefined}
                  onClick={() =>
                    blockRefs.current[i]?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    })
                  }
                  className="group flex cursor-pointer items-center gap-4 text-left"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold transition-colors ${
                      i === active
                        ? "bg-flame text-white"
                        : "text-concrete ring-1 ring-concrete/40 group-hover:ring-flame/60"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={`font-heading transition-colors ${
                      i === active
                        ? "font-bold text-graphite"
                        : "font-medium text-concrete group-hover:text-graphite"
                    }`}
                  >
                    {step.title}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </nav>

        {/* Hairline divider, like the reference */}
        <div aria-hidden className="hidden w-px bg-graphite/10 lg:block" />

        {/* Step blocks */}
        <div className="flex flex-col gap-20 lg:gap-28">
          {PROCESS_DETAIL_STEPS.map((step, i) => (
            <article
              key={step.number}
              ref={(el) => {
                blockRefs.current[i] = el;
              }}
            >
              {/* Mobile keeps the sequence legible without the sidebar */}
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-flame font-heading font-extrabold text-white lg:hidden">
                {step.number}
              </span>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-graphite sm:text-4xl lg:mt-0">
                {step.title}
              </h2>
              <div className="mt-6 flex flex-col gap-5">
                {step.points.map((point) => (
                  <div key={point.heading}>
                    <h3 className="font-heading text-lg font-bold text-graphite">
                      {point.heading}
                    </h3>
                    <p className="mt-1 leading-relaxed text-graphite/70">
                      {point.body}
                    </p>
                  </div>
                ))}
              </div>
              <img
                src={img(step.img, { w: 1200, h: 750 })}
                alt={step.alt}
                width={1200}
                height={750}
                loading="lazy"
                className="mt-8 w-full rounded-2xl object-cover ring-1 ring-graphite/10"
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
